import { useAuth0 } from '@auth0/auth0-react';
import { Breakpoints } from '@unmind/design-system/theme/deprecated';
import { rem } from 'polished';
import React, { ReactNode, RefObject, useRef } from 'react';
import { RouteComponentProps, useLocation, withRouter } from 'react-router';
import { compose } from 'recompose';
import styled, { css } from 'styled-components';
import { zIndex } from 'styles';
import { isMSTeams } from 'utils/MSTeams';
import { useUser } from 'Account/hooks';
import { Container } from '@unmind/design-system';
import InfoBanner from 'Shared/InfoBanner';
import { HeadingText } from 'Shared/Typography';
import { useTitle } from 'Home/PersonalisedToday/Header/useTitle';
import { Namespace } from 'i18next';
import { useTranslation } from 'react-i18next';
import { LayoutConfig } from 'App/routes';
import { WithLogoutProps, withLogout } from '../App/Auth';
import useLogoutWithUniversalLogin from '../App/Auth/useLogoutWithUniversalLogin';
import RoutePath from '../App/RoutePath';
import useActiveUserTracking from '../App/Tracking/useActiveUserTracking';
import { SkipToMainContent } from '../Shared/Accessibility';
import BackToTop from '../Shared/BackToTop';
import { extraSmall, small, UserType } from '../utils';
import {
  LARGE_HEADER_HEIGHT,
  SMALL_HEADER_HEIGHT,
  SMALL_HEIGHT,
  SMALL_TEAMS_HEADER_HEIGHT,
} from '../utils/headerHeight';
import useViewportWidth from '../utils/useViewportWidth';
import AccountMenu from './AccountMenu';
import MainMenu from './MainMenu';
import { Logo } from './Logo';

export const HiddenOnMobile = styled.div`
  display: none;

  ${extraSmall(css`
    display: flex;
    flex: 1;
  `)}
`;

const HiddenOnWeb = styled.div`
  display: flex;

  ${extraSmall(css`
    display: none;
  `)}
`;

const Header = styled('header')<{ disablePositionFixed: boolean }>`
  /* Allow overriding position: fixed for Storybook */
  position: ${({ disablePositionFixed }) =>
    disablePositionFixed ? 'static' : 'fixed'};
  top: 0;
  width: 100%;
  z-index: ${zIndex.header};
`;

const PageTitle = styled(HeadingText).attrs(({ theme }) => ({
  level: 1,
  sizes: [theme.typography.fontSizes.fontSize24],
  weight: theme.typography.fontWeights.medium,
}))`
  text-align: left;
  margin: ${rem(12)} ${rem(16)} ${rem(12)} 0;
`;

const NavItemWrapper = styled.div<{
  alignment: 'left' | 'center' | 'right';
  displayedInMSTeams?: boolean;
}>`
  flex: ${({ displayedInMSTeams }) => (displayedInMSTeams ? 2 : 1)};
  width: 100%;
  display: flex;
  justify-content: ${({ alignment }) =>
    alignment === 'left'
      ? 'flex-start'
      : alignment === 'center'
        ? 'center'
        : 'flex-end'};
`;

export const HeaderInner = styled.div<{ displayedInMSTeams: boolean }>`
  display: flex;
  flex-direction: row;
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.border.secondary}`};
  height: ${({ displayedInMSTeams }) =>
    displayedInMSTeams
      ? rem(SMALL_TEAMS_HEADER_HEIGHT)
      : rem(SMALL_HEADER_HEIGHT)};

  ${small(css`
    height: ${rem(LARGE_HEADER_HEIGHT)};
  `)}
`;

const AppContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100vh;
`;

const StyledContainer = styled(Container)`
  display: flex;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  flex: 1;
`;

const HeaderMainMenuWrapper = styled.div`
  align-items: stretch;
`;

const IE11OnlyWrapper = styled.div`
  display: none;
`;

const HeaderMainMenu = () => {
  const viewportWidth = useViewportWidth();
  // If the viewport is bigger than a mobile device or if the web-app is in MS Teams regardless of viewport, render the nav menu in the header
  // This is because MS Teams' normal nav-bar is on the bottom and can't clash with ours
  if (viewportWidth >= Breakpoints.XS || isMSTeams()) {
    return (
      <HeaderMainMenuWrapper>
        <MainMenu isInFooter={false} orientation="horizontal" />
      </HeaderMainMenuWrapper>
    );
  }

  return null;
};

const SidebarContainer = styled.div`
  display: block;
  width: ${rem(255)};
  height: 100%;
  transition: width 0.3s ease;

  ${extraSmall(css`
    min-width: ${rem(255)};
  `)}
`;

interface HeaderTitleProps {
  firstName: string | undefined;
}

const HeaderTitle = ({ firstName }: HeaderTitleProps) => {
  const location = useLocation();
  const pathname = location.pathname;
  const { t: translate } = useTranslation<
    Namespace<'explore' | 'track' | 'book'>
  >(['explore', 'track', 'book']);
  const title = useTitle(firstName);

  const renderSwitch = () => {
    switch (pathname) {
      case '/book':
      case '/book/practitioners':
        return translate('book:book.metaTitle');
      case '/explore':
        return translate('explore:title');
      case '/home':
        return title;
      case '/track':
        return translate('track:title');
      default:
        return null;
    }
  };

  return <PageTitle>{renderSwitch()}</PageTitle>;
};

const MainContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  transition:
    margin-left 0.3s ease,
    width 0.3s ease;
`;

export const AuthenticatedContent = styled.main`
  display: block;
  height: 100%;
  width: 100%;
  padding-top: ${rem(SMALL_HEADER_HEIGHT)};
  padding-bottom: ${rem(SMALL_HEIGHT)};

  ${extraSmall(css`
    padding-bottom: 0;
    padding-left: 0;
    padding-top: 0;
  `)}

  ${small(css`
    padding-top: 0;
  `)}
`;

// This is required as setting `height: 100%` doesn't work
// for childen of an element (such `AuthenticatedContent`) that
// has `min-height: 100%`.
// For smallest phone screens 100% is necessary to see full screen
export const FullHeightContent = styled.div`
  width: 100%;
  height: 100%;

  ${extraSmall(css`
    height: 100vh;
  `)}

  ${small(css`
    height: 100vh;
  `)}
`;

const AppFooter = styled.div`
  position: fixed;
  bottom: 0;
  display: flex;
  width: 100%;
  align-items: stretch;
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-top: ${({ theme }) =>
    `${rem(1)} solid ${theme.colors.border.secondary}`};
  z-index: ${zIndex.overModal};
  height: ${rem(SMALL_HEIGHT)};
`;

interface AuthenticatedFooterProps {
  headerLogoRef: RefObject<HTMLAnchorElement>;
  fullscreen?: boolean;
}

export const AuthenticatedFooter = ({
  headerLogoRef,
  fullscreen,
}: AuthenticatedFooterProps) => {
  const viewportWidth = useViewportWidth();

  // If the viewport is a small mobile device and the web-app is not in MS Teams, render the nav menu in the footer
  if (viewportWidth < Breakpoints.XS && !isMSTeams() && !fullscreen) {
    return (
      <AppFooter id="app-footer">
        <MainMenu isInFooter={true} orientation="horizontal" />
      </AppFooter>
    );
  }

  return <BackToTop onClickTargetRef={headerLogoRef} />;
};

interface AuthenticatedPageProps extends RouteComponentProps, WithLogoutProps {
  layoutConfig?: LayoutConfig;
  children: ReactNode;
}

export const MAIN_CONTENT_ID = 'maincontent';

interface AuthenticatedHeaderProps {
  permissions: string[] | undefined;
  userType?: UserType;
  firstName: string | undefined;
  disablePositionFixed?: boolean;
  onLogout(): void;
  onNavigate(path: string): void;
}

export const AuthenticatedHeader = ({
  permissions,
  userType,
  firstName,
  disablePositionFixed = false,
  onLogout,
  onNavigate,
}: AuthenticatedHeaderProps) => (
  <Header id="app-header" disablePositionFixed={disablePositionFixed}>
    <IE11OnlyWrapper>
      <InfoBanner
        text="We will no longer be supporting Internet Explorer 11 from 15th June.
      Please upgrade to a newer browser to ensure a great Unmind experience."
      />
    </IE11OnlyWrapper>
    <HeaderInner displayedInMSTeams={isMSTeams()}>
      <StyledContainer>
        {
          // Ensure not to render the title on the MS Teams mobile app as there is no space in the header nav-bar
          !isMSTeams() && (
            <HiddenOnWeb>
              <HeaderTitle firstName={firstName} />
            </HiddenOnWeb>
          )
        }
        <NavItemWrapper alignment="center" displayedInMSTeams={isMSTeams()}>
          <HeaderContent>
            <HeaderMainMenu />
          </HeaderContent>
        </NavItemWrapper>
        <NavItemWrapper alignment="right">
          <HeaderContent>
            <AccountMenu
              permissions={permissions}
              userType={userType}
              onLogout={onLogout}
              onNavigate={onNavigate}
              isInVerticalMenu={false}
            />
          </HeaderContent>
        </NavItemWrapper>
      </StyledContainer>
    </HeaderInner>
  </Header>
);

const AuthenticatedPage = ({
  history,
  logout,
  layoutConfig,
  children,
}: AuthenticatedPageProps) => {
  const viewportWidth = useViewportWidth();
  const headerLogoRef = useRef<HTMLAnchorElement>(null);

  const onNavigate = (path: string) => {
    history.push(path);
  };

  const { isAuthenticated } = useAuth0();
  const { logoutWithUniversalLogin } = useLogoutWithUniversalLogin();
  const { permissions, userType, firstName } = useUser();

  useActiveUserTracking();

  const showVerticalNavigation = viewportWidth >= Breakpoints.XS;
  const fullScreen = layoutConfig?.fullscreen || false;

  return (
    <AppContainer>
      {showVerticalNavigation && !fullScreen && (
        <SidebarContainer id="app-sidebar-menu">
          <MainMenu
            isInFooter={false}
            logoComponent={<Logo headerLogoRef={headerLogoRef} />}
            orientation="vertical"
          />
        </SidebarContainer>
      )}

      <MainContentWrapper>
        <SkipToMainContent />
        {!showVerticalNavigation && !fullScreen && (
          <AuthenticatedHeader
            firstName={firstName}
            onNavigate={onNavigate}
            permissions={permissions}
            userType={userType}
            onLogout={async () => {
              if (isAuthenticated) {
                await logoutWithUniversalLogin();
              } else {
                await logout();

                if (isMSTeams()) {
                  history.replace(RoutePath.StartSplitScreen);
                } else {
                  history.replace(RoutePath.Login);
                }
              }
            }}
          />
        )}

        <AuthenticatedContent id={MAIN_CONTENT_ID}>
          {children}
        </AuthenticatedContent>
        <AuthenticatedFooter
          headerLogoRef={headerLogoRef}
          fullscreen={fullScreen}
        />
      </MainContentWrapper>
    </AppContainer>
  );
};

export default compose<AuthenticatedPageProps, Record<string, unknown>>(
  withLogout,
  withRouter,
)(AuthenticatedPage);
